/**
 * CAMPAIGN W1 (wave 1): the pure campaign engine. Content is DATA — beats live
 * in act0.ts/act1.ts (and later actN.ts) as typed constants; THIS module only
 * defines the shapes and the reducer that walks them.
 *
 * PURE sim-core: no Three.js, no DOM, no Math.random, no Date.now. The client's
 * CampaignDirector owns all side effects (spawning scripted signals, pushing
 * objectives, playing comms, chapter cards); it calls stepCampaign with typed
 * events and reacts only when the returned state is a NEW reference.
 */
import type { Vec3 } from '../vec3.js';
import type { Objective, ObjectiveEvent } from '../objective.js';
import type { SignalKind } from '../signal.js';
import type { FactionId } from '../faction.js';

/** Open-keyed choice/consequence flags (cenotaphChoice, kiraTrust, drumMercy, ...). */
export type CampaignFlags = Record<string, string | boolean>;

export interface CampaignState {
  act: 0 | 1 | 2 | 3;
  beatId: string;
  flags: CampaignFlags;
}

/** Where a scripted signal sits: offset from a manifest body, or absolute. */
export type SignalAnchor = { body: string } | { absolute: Vec3 };

/** A campaign-scripted, never-decaying signal with an authored carved record. */
export interface StorySignalSpec {
  id: string;
  anchor: SignalAnchor;
  offsetKm: Vec3;
  markerOffsetKm?: Vec3;
  kind: SignalKind;
  title: string;
  threat: number;
  seed: number;
  /** Authored carved record — overrides the procedural lore tables, so the
   * verification machinery (contradicts/verifySignal) runs on campaign names. */
  record?: { name: string; log: string | null };
  /** SHIP-EXPANSION: faction-scope this scripted pocket's hostiles. */
  faction?: FactionId;
  /** SHIP-EXPANSION: force this pocket's hostiles to a specific hull (e.g. the
   * Black Cutter interdiction flying Captain Drum's `hellebore`). */
  enemyType?: string;
}

/** One radio line, rendered "CALLSIGN > text" in the comms panel. */
export interface CommsLine {
  speaker: string;
  text: string;
  /** CAMPAIGN VO: explicit voice-asset slug override (filename without the
   * `/audio/voice/` prefix or `.mp3`, e.g. `jem-c-kira-00a`). When a line is
   * inserted into an existing comms the index-derived filenames of every later
   * line would shift; pinning `voice` keeps each already-generated clip mapped to
   * its own line. Absent = the client derives `<slug>-<commsId>-<NN>` from index. */
  voice?: string;
}

/** A 1/2-keyed choice at the end of a comms script. */
export interface CommsChoice {
  key: '1' | '2';
  label: string;
  setFlag?: { key: string; value: string | boolean };
  /** Optional explicit jump; otherwise the beat's advance rules handle CHOICE. */
  goto?: string;
}

export interface CommsScript {
  id: string;
  lines: CommsLine[];
  choices?: CommsChoice[];
}

/** Everything that can advance the campaign. ObjectiveEvents are reused as-is. */
export type CampaignEvent =
  | ObjectiveEvent
  | { type: 'COMMS_DONE'; commsId: string }
  | { type: 'CHOICE'; commsId: string; key: '1' | '2' }
  | { type: 'ENCOUNTER_CLEARED'; signalId: string }
  | { type: 'CRUISE_ENGAGED' }
  // CAMPAIGN §8: the silent Cenotaph Choice. No comms menu — the player's hand
  // makes it: [V] finishes the K (`finished`), undocking leaves it (`left`). The
  // event carries the value; the reducer writes `cenotaphChoice` and advances.
  | { type: 'CENOTAPH_CHOICE_MADE'; value: 'finished' | 'left' };

/** Beat-local advance rule: event type (+ optional target id) → next beat. */
export interface AdvanceRule {
  on: CampaignEvent['type'];
  /** signalId / stationId / commsId the event must carry ('' or absent = any). */
  target?: string;
  next: string;
}

export interface CampaignBeat {
  id: string;
  act: 0 | 1 | 2 | 3;
  /** Full-screen card shown on beat entry (pauses the sim like DOCKED). */
  chapterCard?: { title: string; sub?: string };
  /** Objective pushed on beat entry (reuses objective.ts verbatim). */
  objective?: Objective;
  /** Scripted signals alive while this beat is current. */
  storySignals?: StorySignalSpec[];
  /** Optional HUD nav-marker hint: the id of a storySignal this beat should point
   * the player at on entry (heading bracket + off-screen arrow via the existing
   * signal nav-lock). Distinct from `objective.target` — a `bounty` objective keeps
   * target:'' (it matches on faction), so the location lives here, not in target. */
  markerSignalId?: string;
  /** Radio script played on beat entry (diegetic — does not pause flight). */
  comms?: CommsScript;
  /** Sombre side-effect on entry: name someone on the Roll + the death tone. */
  rollEntry?: string;
  advance: AdvanceRule[];
}

/** The target id an event carries (for AdvanceRule matching). */
const eventTarget = (ev: CampaignEvent): string => {
  switch (ev.type) {
    case 'DOCKED_AT':
      return ev.stationId;
    case 'DROPPED_AT_SIGNAL':
    case 'RESCUED':
    case 'VERIFIED':
      return ev.signalId;
    case 'ENCOUNTER_CLEARED':
      return ev.signalId;
    case 'COMMS_DONE':
    case 'CHOICE':
      return ev.commsId;
    case 'KILLED_NPC':
      return ev.faction;
    case 'SCOOPED':
    case 'CRUISE_ENGAGED':
    case 'CENOTAPH_CHOICE_MADE':
      return '';
  }
};

/** Find a beat by id (undefined when absent — the reducer treats that as halt). */
export const beatById = (
  beats: readonly CampaignBeat[],
  id: string,
): CampaignBeat | undefined => beats.find((b) => b.id === id);

/**
 * THE reducer. Pure. Returns the SAME reference when the event does not move
 * the campaign (cheap dirty-check for the director). CHOICE events first merge
 * their flag, then honor the choice's `goto` (falling back to the beat's
 * advance rules for `on: 'CHOICE'`).
 */
export const stepCampaign = (
  state: CampaignState,
  ev: CampaignEvent,
  beats: readonly CampaignBeat[],
): CampaignState => {
  const beat = beatById(beats, state.beatId);
  if (!beat) return state;

  let flags = state.flags;
  let forcedNext: string | undefined;
  if (ev.type === 'CHOICE' && beat.comms && beat.comms.id === ev.commsId) {
    const choice = beat.comms.choices?.find((c) => c.key === ev.key);
    if (choice?.setFlag) flags = { ...flags, [choice.setFlag.key]: choice.setFlag.value };
    forcedNext = choice?.goto;
  }
  // CAMPAIGN §8: the silent Cenotaph Choice writes its flag from the event value
  // (byte-identical keys/values to the old CHOICE menu, so saves stay compatible).
  if (ev.type === 'CENOTAPH_CHOICE_MADE') {
    flags = { ...flags, cenotaphChoice: ev.value };
  }

  const rule = beat.advance.find(
    (r) => r.on === ev.type && (!r.target || r.target === eventTarget(ev)),
  );
  const nextId = forcedNext ?? rule?.next;
  if (!nextId) {
    // A flag merge without a move still produces a new state (flags changed).
    return flags === state.flags ? state : { ...state, flags };
  }
  const nextBeat = beatById(beats, nextId);
  if (!nextBeat) return flags === state.flags ? state : { ...state, flags };
  return { act: nextBeat.act, beatId: nextBeat.id, flags };
};

/** Fresh campaign at the first beat of the table. */
export const initialCampaignState = (beats: readonly CampaignBeat[]): CampaignState => ({
  act: beats[0]?.act ?? 0,
  beatId: beats[0]?.id ?? '',
  flags: {},
});

/**
 * Content-integrity gate (run in tests for EVERY act table, now and future):
 * unique ids, every advance/goto target exists, acts never decrease along
 * declaration order, and every beat is reachable from the first.
 */
export const validateBeatTable = (beats: readonly CampaignBeat[]): string[] => {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const b of beats) {
    if (ids.has(b.id)) errors.push(`duplicate beat id: ${b.id}`);
    ids.add(b.id);
  }
  let lastAct = -1;
  for (const b of beats) {
    if (b.act < lastAct) errors.push(`act decreases at beat ${b.id}`);
    lastAct = Math.max(lastAct, b.act);
    for (const r of b.advance) {
      if (!ids.has(r.next)) errors.push(`beat ${b.id} advances to missing ${r.next}`);
    }
    for (const c of b.comms?.choices ?? []) {
      if (c.goto && !ids.has(c.goto)) errors.push(`beat ${b.id} choice goto missing ${c.goto}`);
    }
  }
  // Reachability from the first beat (advance edges + choice gotos).
  if (beats.length > 0) {
    const reach = new Set<string>([beats[0]!.id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const b of beats) {
        if (!reach.has(b.id)) continue;
        for (const r of b.advance) if (!reach.has(r.next)) { reach.add(r.next); grew = true; }
        for (const c of b.comms?.choices ?? [])
          if (c.goto && !reach.has(c.goto)) { reach.add(c.goto); grew = true; }
      }
    }
    for (const b of beats) if (!reach.has(b.id)) errors.push(`beat ${b.id} unreachable`);
  }
  return errors;
};
